import { parsePathSteps } from "csi/core/extendedPathService";
import { Context } from "csi/def/Context";
import csi from "csi/main";
import reflectionHints from "csi/reflectionHints";
import { C8AttributeId, C8Node, C8NodeType, cnl } from "csi/typings";
import xml from "csi/xml";
import _ from "lodash";
import { computed } from "mobx";
import ReflectionAttribute from "./ReflectionAttribute";
import ReflectionDomainClass from "./ReflectionDomainClass";
import ReflectionEnum from "./ReflectionEnum";
import { getClassUrl } from "./reflection";
import {
	ATTR_CURRENT_REVISION,
	ATTR_LATEST_REVISION,
	ATTR_MASTER,
	ATTR_MODIFIED_AT,
	ATTR_MODIFIED_BY,
	ATTR_PHASE_REVISIONS,
	ATTR_REVISIONS,
	PATH_CURRENT_REVISION,
	PATH_PARENT,
	TYPE_DOC_AND_COMMENT_OWNER,
	TYPE_IMAGE,
	TYPE_IMAGE_CONTAINER,
	TYPE_REVISION,
	TYPE_SUBTYPE,
	TYPE_SUBTYPED_CLASS,
	TYPE_TAG,
	TYPE_UOM_CONVERSION,
	URL_BO
} from "./reflectionConstants";
import { createAttributeGroups, getAttributeByClassNameAndId, getClass, getClassesByTypes, getDomainClass, getEnumList } from "./reflectionData";
import { AttributeGroup, ReflectionAttrData, ReflectionClassData, ReflectionRelationData } from "./reflectionTypes";
import { CLASS_UNIT_OF_MEASURE, CLASS_UOM_CONVERSION, CLASS_UOM_MODULE, CLASS_UOM_TYPE } from "src/c8-platform/type/uom";
import { pathCreate } from "csi/utility/pathUtility";

const RE_REVISION = /Revision$/;
const TYPE_SINGLETON = "_CS_Singleton";
const setOmitTypesFromPreferencePaths = new Set([TYPE_IMAGE, TYPE_TAG, TYPE_UOM_CONVERSION]);

type OnStepFunc = (rc?: ReflectionClass, rcPrev?: ReflectionClass, id?: string, bParent?: boolean, bFirstClass?: boolean, stepKey?: string | number) => void;
type ReflectionClassTraverseFunction = (rc: ReflectionClass, bDerived: boolean) => (boolean | void);

class ReflectionClass {
	/** domain class containing class - could be undefined if abstract */
	domainClass?: ReflectionDomainClass;

	/** indicates if this class, or any of its derived classes when abstract, is a secondary class */
	isSecondary = false;

	readonly attributeGroups: AttributeGroup[];
	readonly data: ReflectionClassData;
	private _super: ReflectionClass[];
	private _derived: ReflectionClass[];
	private _concrete: ReflectionClass[];
	private _attributes: Record<string, ReflectionAttribute>;
	private _relations: Record<string, ReflectionAttribute>;
	private _incomingRelations: Record<string, ReflectionAttribute[]>;
	protected _typeRevision: string;
	protected _typeMaster: string;
	private _rollupExpressions: Record<C8AttributeId, string> = null;

	get concreteClasses(): ReflectionClass[] {
		return this._concrete || [];
	}

	get derivedClasses(): ReflectionClass[] {
		return this._derived || [];
	}

	get subtype(): C8NodeType | undefined {
		return this.isDerivedFrom(TYPE_SUBTYPED_CLASS) ? `${this.type}${TYPE_SUBTYPE}` : undefined;
	}

	get superClasses(): ReflectionClass[] {
		return this._super || [];
	}

	get type(): C8NodeType {
		return this.data[0];
	}

	private get attributes(): ReflectionAttrData[] {
		return this.data[1];
	}

	private get outgoingRelationships(): ReflectionRelationData[] {
		return this.data[2];
	}

	private get incomingRelationships(): string[] {
		return this.data[3];
	}

	get immediateSuperTypes(): C8NodeType[] {
		return this.data[4] || [];
	}

	get immediateDerivedTypes(): C8NodeType[] {
		return this.data[5] || [];
	}

	get alias(): string {
		return this.data[6];
	}

	get abstract(): boolean {
		return !!this.data[7];
	}

	/** indicates if class is abstract or disabled which is used to determine, for example, if instances of this class should be created */
	get abstractOrDisabled(): boolean {
		return this.abstract || this.disabled;
	}

	/** indicates if class can be selected as the target class of a ref attribute */
	get canSelectAsTargetClass(): boolean {
		const { enabled, isUOMClass, startsWithUnderscore } = this;
		return enabled && !this.isSingleton() && !isUOMClass && !startsWithUnderscore;
	}

	get rPrivate(): boolean {
		return !!this.data[8];
	}

	get disabled(): boolean {
		return !this.enabled;
	}

	get enabled(): boolean {
		return !this.data[9];
	}

	get classValidation(): boolean {
		return !!this.data[10];
	}

	/** list of preference paths coming from custom ref attributes */
	get customPreferencePaths(): string[] {
		return this.getRelations()
			.filter((ra) => ra.isCustom && !ra.isHidden && ra.isValidPreferencePathRelation)
			.map(({ attributeId }) => pathCreate(attributeId));
	}

	get hasImageLabels(): boolean {
		return !!this.imageLabelEnum?.activeValues?.length;
	}

	get imageLabel(): string {
		return this.data[11];
	}

	get imageLabelEnum(): ReflectionEnum {
		return getEnumList(this.imageLabel);
	}

	/** indicates if class is part of the UOM model */
	get isUOMClass(): boolean {
		const { type } = this;
		return type === CLASS_UNIT_OF_MEASURE ||
			type === CLASS_UOM_CONVERSION ||
			type === CLASS_UOM_MODULE ||
			type === CLASS_UOM_TYPE;
	}

	/** indicates if class is a valid destination for preference paths (along a path) */
	get isValidPreferencePathDestination(): boolean {
		return !this.abstract && !setOmitTypesFromPreferencePaths.has(this.type);
	}

	get revisionType(): string {
		return this.data[13];
	}

	/** indicates if class can be selected as the target class of a ref attribute */
	get startsWithUnderscore(): boolean {
		return this.type.startsWith("_");
	}

	get m_type(): string {
		return this.type;
	}

	constructor(data?: ReflectionClassData) {
		this.data = data;
		this.attributeGroups = createAttributeGroups(data[12]);
	}

	/**
	 * First stage of initialization of class members
	 */
	init(): void {
		let a: ReflectionClass[];
		let b: ReflectionClass[];
		let c: ReflectionClass;
		let i: number;

		// find all nested super and derived classes that will be used for traversing class hierarchy
		if (a = getClassesByTypes(this.immediateSuperTypes)) {
			i = 0;

			while (c = a[i++]) {
				if (b = getClassesByTypes(c.immediateSuperTypes)) {
					a = a.concat(b);
				}
			}

			this._super = _.uniq<ReflectionClass>(a);

			if (!this._super.length) {
				this._super = null;
			}
		}

		if (a = getClassesByTypes(this.immediateDerivedTypes)) {
			i = 0;

			while (c = a[i++]) {
				if (b = getClassesByTypes(c.immediateDerivedTypes)) {
					a = a.concat(b);
				}
			}

			this._derived = _.uniq(a);
			this._concrete = _.filter(this._derived, (rc) => !rc.abstract);

			if (!this._derived.length) {
				this._derived = null;
			}

			if (!this._concrete.length) {
				this._concrete = null;
			}
		}

		// hash attributes and relations by id for quick lookup
		let attrs = this._attributes = {};

		this.attributes?.forEach((attrDef) => {
			const attr = new ReflectionAttribute(attrDef, this);
			attrs[attr.attributeId] = attr;
		});

		attrs = this._relations = {};
		this.outgoingRelationships?.forEach((attrDef) => {
			var attr = new ReflectionAttribute(attrDef, this);
			attrs[attr.attributeId] = attr;
		});

		// Force __Parent__ relationship to not be hidden unless there is another relationship that takes its place;
		// FIX: need to determine right way to do this, but right now if done in model then most all nodes will have
		// to be migrated to update this flag since it is not reflection flag
		if (attrs[xml.ATTR_PARENT] && !reflectionHints.getParentReplacement(this.m_type)) {
			attrs[xml.ATTR_PARENT].forceVisible = true;
		}

		this._initMasterRevision(this.revisionType);
	}

	/**
	 * Second stage of initialization that relies on all classes going through first stage of initialization
	 */
	init2(): void {
		// incoming relations may not have unique ids, so _incomingRelations can map to array of attributes. When trying to find
		// a single relation when there are multiple, then we will return null. If trying to find many, then we can return all.
		this._incomingRelations = csi.hashArray(
			this.incomingRelationships,
			(p) => p[1],
			(p) => getAttributeByClassNameAndId(p[0], p[1]),
			null,
			true,
		) || {};

		// add super classes of derived classes to ensure traverse method goes through all necessary classes
		const derived = this._derived;
		if (derived) {
			const obj = {};
			const extras = [];

			obj[this.m_type] = true;
			csi.hashArray(this._super, "m_type", Boolean, obj);
			csi.hashArray(derived, "m_type", Boolean, obj);

			_.each(derived, (rc) => {
				_.each(rc._super, (rcSuper) => {
					var type = rcSuper.getType();

					if (!obj[type]) {
						obj[type] = true;
						extras.push(rcSuper);
					}
				});
			});

			this._derived = derived.concat(extras);
		}
	}

	/**
	 * Apply function to this class and possibly super and derived cla sses;
	 * return class for which function returns true (if any)
	 */
	traverse(bSuper: boolean, bDerived: boolean, f: ReflectionClassTraverseFunction, scope?: any): ReflectionClass | null {
		//TODO remove win.global
		scope = scope || window.global;
		// test this class
		if (f.call(scope, this, false)) {
			return this;
		}
		// test super classes
		let a = bSuper ? this._super : null;
		let n = a ? a.length : 0;
		for (let i = 0; i < n; ++i) {
			if (f.call(scope, a[i], false)) {
				return a[i];
			}
		}
		// test derived classes
		a = bDerived ? this._derived : null;
		n = a ? a.length : 0;
		for (let i = 0; i < n; ++i) {
			if (f.call(scope, a[i], true)) {
				return a[i];
			}
		}
		return null;
	}

	protected _initMasterRevision(revType: string): void {
		// if master, add appropriate path mappings to aid in path traversal
		if (revType) {
			const masterType = this.m_type;
			this._typeRevision = revType;

			// add appropriate path mappings to aid in traversal if an abstract class - in this case the class
			// will not be in the domain model and so we cannot extract the true destination classes
			if (this.abstract) {
				reflectionHints.setTarget(revType, xml.ATTR_PARENT, masterType);
				reflectionHints.setTarget(revType, ATTR_MASTER, masterType);
				reflectionHints.setTarget(revType, ATTR_CURRENT_REVISION, revType);

				reflectionHints.setTarget(masterType, ATTR_MASTER, masterType);
				reflectionHints.setTarget(masterType, ATTR_REVISIONS, revType);
				reflectionHints.setTarget(masterType, ATTR_CURRENT_REVISION, revType);
				reflectionHints.setTarget(masterType, ATTR_LATEST_REVISION, revType);
				reflectionHints.setTarget(masterType, ATTR_PHASE_REVISIONS, revType);
			}

			// add access mappings to aid in determining if attributes are editable along a path
			reflectionHints.setAccess(revType, xml.ATTR_PARENT, Boolean);
			reflectionHints.setAccess(revType, ATTR_MASTER, Boolean);
			reflectionHints.setAccess(revType, ATTR_CURRENT_REVISION, Boolean);

			reflectionHints.setAccess(masterType, ATTR_MASTER, Boolean);
			reflectionHints.setAccess(masterType, ATTR_REVISIONS, Boolean);
			reflectionHints.setAccess(masterType, ATTR_CURRENT_REVISION, Boolean);
			reflectionHints.setAccess(masterType, ATTR_LATEST_REVISION, Boolean);
			reflectionHints.setAccess(masterType, ATTR_PHASE_REVISIONS, Boolean);
		} else if ((this.m_type != TYPE_REVISION) && this.m_type.match(RE_REVISION)) {
			this._typeMaster = this.m_type.replace("Revision", "");
		}
	}

	toString(): string {
		return this.m_type;
	}

	getAlias(): string | null {
		return this.alias;
	}

	getType(): string {
		return this.m_type;
	}

	getURL(): cnl {
		return getClassUrl(this.m_type);
	}

	getBusinessObject(): cnl | null {
		const alias = this.alias;
		return alias ? (URL_BO + alias) : null;
	}

	/**
	 * Return path to image container for given class, if one is supported
	 */
	getImagePath(): string | null {
		let path = "";
		let rc: ReflectionClass = this;

		if (!rc.isImageContainer) {
			if (rc.isMaster()) {
				rc = rc.getRevisionClass();
				path = PATH_CURRENT_REVISION;
			} else if (rc.isRevision()) {
				rc = rc.getMasterClass();
				path = PATH_PARENT;
			}

			if (!rc.isImageContainer) {
				return null;
			}
		}

		return path;
	}

	getRevisionType(): string {
		return this.revisionType;
	}

	/**
	 * Return true if class is derived from ImageContainer
	 */
	@computed get isDocAndCommentOwner(): boolean {
		return this.isDerivedFrom(TYPE_DOC_AND_COMMENT_OWNER);
	}

	/**
	 * Return true if class is derived from ImageContainer
	 */
	@computed get isImageContainer(): boolean {
		return this.isDerivedFrom(TYPE_IMAGE_CONTAINER);
	}

	isDerivedFrom(rc: string | ReflectionClass): boolean {
		// summary: determine if this class is derived from given class
		if (typeof rc === "string") {
			rc = getClass(rc);
		}

		// special case if two are the same - returning true is the safe way to go
		if (!rc) return false;
		if (rc == this) return true;

		return this._super ? (this._super.indexOf(rc) >= 0) : false;
	}

	getRollupExpression(id: string): string {
		// summary: return expression for attribute if it has not already failed evaluation
		if (!this._rollupExpressions) {
			// cache rollup expressions for all attributes of this class and super classes
			const exps = this._rollupExpressions = {};
			let exp: string;

			const hashFunction = function (obj): void {
				for (var id in obj) {
					if (!exps[id]) {
						if (exp = obj[id].getExpression()) {
							exps[id] = exp;
						}
					}
				}
			};

			this.traverse(true, false, (rc) => {
				hashFunction(rc._attributes);
				hashFunction(rc._relations);
			}, this);
		}

		return this._rollupExpressions[id];
	}

	hasCompositePath(path: string, rcParent?: ReflectionClass): boolean {
		// summary: return true if all steps of path are composite
		let b = true;

		this.getPathClass(path, false, rcParent, (rcNext, rcPrevious, idRelation, bChild) => {
			if (b) {
				var rcr = bChild ? rcPrevious.getRelation(idRelation, true, true) : null;
				b = rcr && rcr.isComposite;
			}
		});

		return !!b;
	}

	hasDerivedClasses(): boolean {
		return !!this._derived?.length;
	}

	@computed get hasNodeValidation(): boolean {
		// summary: class has validation if itself or any base class has validation rules
		return this.classValidation || this._super?.some((rc) => rc.hasNodeValidation);
	}

	/**
	 * Treat class as localized item if it has LocalizedName attribute that is enabled and of proper format
	 */
	@computed get isLocalizedItem(): boolean {
		const ra = this.getAttribute(xml.ATTR_LOCALIZED_NAME, true);
		return !!ra?.isEnabled && (ra?.getFormat() === "Localized");
	}

	isMaster(): boolean {
		return !!this._typeRevision;
	}

	isRevision(): boolean {
		return !!this._typeMaster;
	}

	isSingleton(): boolean {
		return this.isDerivedFrom(TYPE_SINGLETON);
	}

	isRelationEditable(id: string, node?: C8Node, nodeTarget?: C8Node, nodeStart?: C8Node, ctx?: Context): boolean {
		// summary: determine if relation is editable

		// lookup in path map for functions to determine access based on node
		let f = reflectionHints.getAccess(this.m_type, id);
		if (f == null) {
			// lookup actual relation and owner class
			var rcr = this.getRelation(id, true, true);
			var rc = rcr ? rcr.getOwner() : null;
			if (rc) {
				// if owner of relation is different than this class, then lookup again
				if (rc.m_type != this.m_type) {
					f = reflectionHints.getAccess(rc.m_type, id);
				}

				// if we didn't find access yet, maybe it will lie in one of the super classes
				if (f == null) {
					this.superClasses.some((rc2) => {
						// if owner of relation is different than this class, then lookup again
						if (f == null) {
							f = reflectionHints.getAccess(rc2.m_type, id);
							return !!f;
						}
					});
				}

				// if still nothing mapped, allow editing if relation is composite
				if (f == null) {
					f = rcr.isComposite ? Boolean : false;
				}
			} else {
				// if relation not in model, allow editing in case Admin UI related
				f = Boolean;
			}

			// save result for future lookups
			reflectionHints.setAccess(this.m_type, id, f);
		}

		if (f == Boolean) {
			return true;
		} else if (!f || !node) {
			return false;
		}

		// TODO - cast to AccessFunction once reflectionHints converted to Typescript
		const accessFunction = f as any;
		return !!accessFunction(node, nodeStart, nodeTarget, ctx);
	}

	/**
	 * Return true if class inherits from SubtypedClass
	 */
	get isSubtyped(): boolean {
		return this.isDerivedFrom(TYPE_SUBTYPED_CLASS);
	}

	/**
	 * Determines if the current class has a common base class with the identified class
	 *
	 * @param {String|Class} rc - Class to compare base classes
	 * @return {Boolean} - true if the classes share a base class together, otherwise false
	 */
	hasCommonBaseClassWith(rc: string | ReflectionClass): boolean {
		if (typeof rc === "string") {
			rc = getClass(rc);
		}

		// special case if two are the same - returning true is the safe way to go
		if (!rc) return false;
		if (rc == this) return true;
		if (rc instanceof ReflectionClass) {
			if (!this._super || !rc._super) return false;
			return _.some(rc._super, (rcsup) => (this._super.indexOf(rcsup) >= 0));
		}
		return false;
	}

	getChildClass(idChild: C8AttributeId, bSingle?: boolean): ReflectionClass {
		const relation = this.getRelation(idChild, true, true);
		if (relation && (!bSingle || relation.isSingle)) {
			return relation.getDestinationClass(this);
		}

		return null;
	}

	getParentClass(): ReflectionClass {
		const relation = this.getRelation(xml.ATTR_PARENT, true, this.abstract);
		return relation ? relation.getDestinationClass(this) : null;
	}

	getAttributes(bRelations?: boolean, bSuper?: boolean, bDerived?: boolean, rcOmit?: ReflectionClass): ReflectionAttribute[] {
		const result: Record<string, ReflectionAttribute> = {};
		const dups: Record<string, ReflectionAttribute> = {};
		let omitted: Record<string, boolean> = {};
		let bTrackDuplicates = false;

		function hash(attrs: Record<string, ReflectionAttribute>): void {
			for (const [id, ra] of Object.entries(attrs)) {
				// ignore omitted attributes
				if (omitted[id]) {
					continue;
				}

				if (!result[id]) {
					// before adding attribute
					result[id] = ra;
				} else if (bTrackDuplicates) {
					// track duplicated attributes on derived classes - only use them if there is one (otherwise use base version)
					dups[id] = (dups[id] === undefined) ? ra : null;
				}
			}
		}

		// the use case here is getting attributes from a derived class when attributes from the base class have already been processed;
		// so we want to omit any attributes that have already been added from the base class so as not to get duplicates
		// (example - Sample and ProductSample)
		if (rcOmit) {
			omitted = csi.hashArray(rcOmit.getAttributes(bRelations, true), "m_id", Boolean);
			omitted[ATTR_MODIFIED_AT] = true;
			omitted[ATTR_MODIFIED_BY] = true;
		}

		this.traverse(bSuper, bDerived, (rc, bDerivedClass) => {
			// omit attributes from any classes at or below the omitted class
			if (!rcOmit || !rcOmit.isDerivedFrom(rc)) {
				bTrackDuplicates = bDerivedClass;
				hash(rc._attributes);

				if (bRelations) {
					hash(rc._relations);
				}
			}
		});

		// process duplicates
		for (const [id, ra] of Object.entries(dups)) {
			if (ra) {
				result[id] = ra;
			}
		}

		return Object.values(result);
	}

	getRelation(idRelation: string, bSuper?: boolean, bDerived?: boolean, bIncoming?: boolean, rcIncomingSource?: ReflectionClass): ReflectionAttribute {
		// FIX: for incoming relationships, should we use getRelations to find all relations and then compare destination class(es)
		// to see if they are all the same before allowing to return one? Since incoming relations are not uniquely named, it is possible
		// there are incoming relations to this class, super, and derived classes that all have different destination classes. In that case,
		// should we pick one, or return null?

		function searchIncoming(rc: ReflectionClass): ReflectionAttribute | undefined {
			const attrs = rc._incomingRelations[idRelation];
			if (attrs) {
				if (rcIncomingSource) {
					// find incoming relationship sourced at given class
					return attrs.find((ra) => ra.getOwner() == rcIncomingSource);
				} else if (attrs.length === 1) {
					// only return incoming relationship if it is uniquely named; use getRelations otherwise to get all incoming relationships with the same name
					return attrs[0];
				}
			}
		};

		function searchOutgoing(rc: ReflectionClass): ReflectionAttribute | undefined {
			return rc._relations[idRelation];
		};

		const searchFunction = bIncoming ? searchIncoming : searchOutgoing;
		let result: ReflectionAttribute;

		this.traverse(bSuper, bDerived, (rc) => {
			result = searchFunction(rc);
			return !!result;
		});

		return result;
	}

	/**
	 * Return relations that this class references (if not bIncoming), or that reference this class (if bIncoming)
	 * @param {boolean} [bSuper] - traverse super classes to find references
	 * @param {boolean} [bDerived] - traverse derived classes to find references
	 * @param {boolean} [bIncoming] - indicate if incoming or outgoing relationships are desired
	 * @param {string} [idRelation] - indicate id of relation that is desired; there could be many relations with same id (if incoming or on super/derived classes)
	 * @return {Attribute[]}
	 */
	getRelations(bSuper?: boolean, bDerived?: boolean, bIncoming?: boolean, idRelation?: C8AttributeId): ReflectionAttribute[] {
		let onTraverseClass: ReflectionClassTraverseFunction;
		let result: ReflectionAttribute[];
		let resultMap: Record<string, ReflectionAttribute>;

		if (idRelation) {
			// if looking for particular relation, then return all relations with that id - even if overridden by derived class
			result = [];

			onTraverseClass = (rc, bDerivedClass): void => {
				// traverse current class if derived (only true if also bDerived which means user wants derived relations),
				// if a result has not yet been found, or if getting incoming relations - this case if necessary because incoming
				// relations may not be unique by id, so it is possible the destination class of the result we already have
				// is different than relations that may exist on the base class
				if (bIncoming) {
					const res = rc._incomingRelations[idRelation];
					if (res) {
						result = result.concat(res);
					}
				} else if (bDerivedClass || !result.length) {
					const res = rc._relations[idRelation];
					if (res) {
						result.push(res);
					}
				}
			};
		} else if (bIncoming) {
			// When looking for incoming relations, a map will be used to identify relations per id and owner type. The first one
			// entered wins (read below for more explanation).
			resultMap = {};

			onTraverseClass = (rc): void => {
				_.each(rc._incomingRelations, (rcas, id) => {
					_.each(rcas, (rca) => {
						const key = id + ":" + rca.getOwner().getType();
						resultMap[key] ||= rca;
					});
				});
			};
		} else {
			// When getting all outgoing relations of a class, then the first one entered wins. If this class is abstract, then the current
			// class and then all super classes will be looked at first. If the relation exists and has not been seen before, then store it.
			// The relation that has already been seen and stored will be more concrete than this duplicate one. Then derived classes are
			// traversed, and in that case we still want to favor the any relations found on this class or its supers, as those will be common
			// across all instances of this class rather than specific to one concrete class.
			resultMap = {};

			onTraverseClass = (rc): void => {
				_.each(rc._relations, (rca, id) => {
					resultMap[id] ||= rca;
				});
			};
		}

		this.traverse(bSuper, bDerived, onTraverseClass);

		if (resultMap) {
			result = _.flatten(_.values(resultMap));
		}

		return result;
	}

	getRelationToType(type: string, bComposite?: boolean, bDerived?: boolean, bSingle?: boolean, bBuiltIn?: boolean): ReflectionAttribute | null {
		var rcTarget = getClass(type);
		if (!rcTarget) {
			return null;
		}

		var rels = this.getRelations(true, true);
		if (!rels) {
			return null;
		}

		var bAbstract = rcTarget.abstract;
		var matchClass = (rc: ReflectionClass): boolean => (rc.m_type == type) || (bAbstract ? rc.isDerivedFrom(type) : false);

		if (bComposite) {
			rels = rels.filter((r) => r.isComposite && (!bSingle || r.isSingle));
		}

		var _rels = rels.filter((r) => r.getDestinationClasses(this).some(matchClass));

		// We might have a situation when we have more than one matching relation type.
		// In the case when user adds custom ref attribute of the same type as existing ref attribute,
		// searching from header may not find correct attribute and use user created custom one instead (C8-6951)
		// If bBuiltIn is true, and if search for specified type returned more than single result, then filter by isCustom
		if (bBuiltIn && (_rels.length > 1)) {
			_rels = _rels.filter((r) => !r.isCustom);
		}

		var rel: ReflectionAttribute;
		if (_rels.length) {
			rel = _rels[0];
		} else if (bDerived) {
			rel = csi.search(rels, (r) => {
				const classes = r.getDestinationClasses(this);
				return classes.some((rc) => rc.traverse(false, true, matchClass));
			});
		}

		return rel;
	}

	getParentPathsToType(type?: C8NodeType, bOmitLastStep?: boolean, bSuper?: boolean, bDerived?: boolean): string[] {
		const paths = {};
		const types = {};

		function followParent(path: string, rc: ReflectionClass): void {
			// prevent recursion
			if (types[rc.m_type]) return;

			types[rc.m_type] = true;

			// look for parent relation
			const rel = rc.getRelation(xml.ATTR_PARENT, bSuper, bDerived);
			if (!rel) return;

			// if found, keep traversing until we reach type
			rel.getDestinationClasses(rc).forEach((rcParent: ReflectionClass) => {
				if (type == rcParent.m_type) {
					if (!bOmitLastStep) {
						path = xml.path(path, xml.ATTR_PARENT);
					}
					paths[path] = true;
				} else {
					followParent(xml.path(path, xml.ATTR_PARENT), rcParent);
				}
			});
		}

		if (type == this.m_type)
			return null;

		followParent("", this);

		return _.isEmpty(paths) ? null : _.keys(paths);
	}

	getMasterClass(): ReflectionClass {
		if (this._typeMaster) {
			return getClass(this._typeMaster);
		} else if (this._typeRevision) {
			return this;
		}

		return null;
	}

	getRevisionClass(): ReflectionClass {
		if (this._typeRevision) {
			return getClass(this._typeRevision);
		} else if (this._typeMaster) {
			return this;
		}

		return null;
	}

	getAttribute(idAttr: string, bSuper?: boolean, bDerived?: boolean, bRelation?: boolean): ReflectionAttribute {
		let result: ReflectionAttribute;

		// don't look for Node Name attribute on derived classes - if not defined on this class or super class, then just assume plain attribute
		// with no special properties
		if (idAttr == xml.ATTR_NAME) {
			bDerived = false;
		}

		this.traverse(bSuper, bDerived, (rc) => {
			var res = rc._attributes[idAttr];
			if (res) {
				result = res;
			} else if (bRelation) {
				if (res = rc._relations[idAttr]) {
					result = res;
				}
			}
			return !!res;
		});

		if (!result && (idAttr == xml.ATTR_NAME)) {
			return null;
		}

		return result;
	}

	/**
	 * Return all concrete classes where the given attribute, or relation if desired, are defined
	 * @param filter - an optional function to filter the list of classes based on the class and attribute
	 */
	getConcreteClassesWithAttribute(id: C8AttributeId, bRelation?: boolean, filter?: (rc: ReflectionClass, ra: ReflectionAttribute) => boolean): ReflectionClass[] {
		const matches = [];

		this.concreteClasses.forEach((rc) => {
			const ra = rc.getAttribute(id, true, false, bRelation);

			if (ra && (!filter || filter(rc, ra))) {
				matches.push(rc);
			}
		});

		return matches;
	}

	getPathClass(path: string, bSingle?: boolean, rcParent?: ReflectionClass, onStep?: OnStepFunc): ReflectionClass {
		const rcs = this.getPathClasses(path, bSingle, rcParent, onStep);
		return rcs?.[0];
	}

	getPathClasses(path: string, bSingle: boolean, rcParent: ReflectionClass, onStep: OnStepFunc): ReflectionClass[] {
		// summary: traverse path from given class, calling onStep callback at each valid step in path if given
		let rcPrevious = [this];
		const steps = parsePathSteps(path);

		steps?.every((step, i) => {
			// parent steps not allowed when looking for single destination
			const {
				a: id,
				c: targetType,
				isParent,
				key,
			} = step;

			if (bSingle && isParent) {
				rcPrevious = null;
				return false;
			}

			const mapTypes = {};
			const rcs = [];

			rcPrevious.forEach((rcPrev) => {
				let classes: ReflectionClass[] = null;

				// first try to get relation for this class or for base classes; only if that does not work, then go to derived classes
				let rels = rcPrev.getRelations(true, false, isParent, id);

				if (!rels || !rels.length) {
					rels = rcPrev.getRelations(false, true, isParent, id);
				}

				if (rels && rels.length) {
					rels.forEach((rel) => {
						if (!bSingle || rel.isSingle || (key != null)) {
							var newClasses = isParent ? rel.getSourceClasses(rcPrev) : rel.getDestinationClasses(rcPrev);
							classes = classes ? classes.concat(newClasses || []) : newClasses;
						}
					});
				} else {
					// support relationships that are not in model - such as those generated through rollup on custom attributes
					const type = reflectionHints.getTarget(rcPrev.getType(), id, isParent);
					if (type != null) {
						const rcHint = getClass(type);
						classes = rcHint ? [rcHint] : [];
					}
				}

				// if first path step is parent and parent class is provided, then use it
				if (!i && !isParent && rcParent && (id == xml.ATTR_PARENT)) {
					if (!classes || classes.some((rc) => rcParent.isDerivedFrom(rc))) {
						classes = [rcParent];
					}
				}

				const n = classes?.length || 0;

				if (n) {
					let bFirstClass = true;

					// if there are multiple classes, and the step target type is known, then narrow the class list
					// to that type if at least one of the classes is derived from that type, or that type is derived from one of the classes
					if (targetType) {
						const rcTarget = getClass(targetType);
						const validClass = classes.some((rc) => rc.isDerivedFrom(rcTarget) || rcTarget.isDerivedFrom(rc));
						classes = validClass ? [rcTarget] : [];
					}

					// iterate through classes
					classes.forEach((rc: ReflectionClass) => {
						// call callback for each destination class of relationship
						if (onStep) {
							onStep(rc, rcPrev, id, !isParent, bFirstClass, key);
							bFirstClass = false;
						}

						if (!mapTypes[rc.m_type]) {
							mapTypes[rc.m_type] = rc;
							rcs.push(rc);
						}
					});
				} else {
					// if there is no class reachable through the current step
					// then call callback with the 'null' class to indicate it
					onStep?.(null, rcPrev, id, !isParent, true, key);
				}
			});

			if (rcs.length) {
				rcPrevious = rcs;
				return true;
			}

			rcPrevious = null;
			return false;
		});

		return rcPrevious;
	}

	/**
	 * Get the classes that have relations to this class
	 * @param subtypeName - if specified, then only find relations mapped to that subtype
	 * @param bVisible - if true, then only return relations that are visible (not hidden)
	 */
	getIncomingOwnerClasses(subtypeName?: string, bVisible?: string): ReflectionClass[] {
		var classMap = {};

		_.each(this._incomingRelations, (rcas, id) => {
			_.each(rcas, (rca: ReflectionAttribute) => {
				if (bVisible && rca.isHidden) {
					return;
				}

				if (subtypeName && (subtypeName != rca.subtypeName)) {
					return;
				}

				var rcOwner = rca.m_rcOwner;
				if (rcOwner.abstract) {
					// add all concrete classes
					_.each(rcOwner.getConcreteClasses(), (rc) => {
						classMap[rc.getType()] = rc;
					});
				} else {
					classMap[rcOwner.getType()] = rcOwner;
				}
			});
		});

		return _.values(classMap);
	}

	/**
	 * Return true if class is abstract and all concrete classes are domain classes
	 */
	get isAbstractClassOfDomainClasses(): boolean {
		return this.abstract && _.every(this.getConcreteClasses(), (rc) => rc.isPrimaryClass);
	}

	getConcreteClasses(): ReflectionClass[] | null {
		// summary: find all derived classes that are not abstract
		return this._concrete;
	}

	/**
	 * Return true if this class is primary class
	 */
	get isPrimaryClass(): boolean {
		return !!getDomainClass(this.m_type);
	}

	isPrivate(): boolean {
		return this.rPrivate;
	}
}

export = ReflectionClass;